/**
 * Helpers for the per-campaign URL convention.
 *
 * The portal exposes every program-scoped page under `/:program/<leaf>` (see
 * App.tsx). The "program" segment is a route slug derived from the campaign id
 * by `routeSlugOf(campaign)` in `pages/flight-deck/analytics.ts`. The Header
 * chip strip and a URL→store sync hook both need to reason about this
 * pattern, so the shared logic lives here.
 */
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSetupCampaigns } from '@state/useSetupCampaigns';
import { routeSlugOf } from '@pages/flight-deck/analytics';

/**
 * Leaf segments that live under `/:program/` per App.tsx. When the current
 * path matches /{slug}/{leaf}[/...], the chip strip rewrites the slug; when
 * the URL changes (direct nav, browser back), the sync hook re-pins the
 * store's selectedCampaignId.
 *
 * Kept as a `Set` for O(1) lookup at every chip click and route change.
 */
export const PROGRAM_SCOPED_LEAVES: ReadonlySet<string> = new Set([
  'flight-deck',
  'distribution',
  'population',
  'predictive',
  'clinical',
  'sentiment',
  'results-notification',
  'document-library',
  'ad-hoc-dispatch',
  'operations',
  'imports',
]);

/**
 * If `pathname` is `/{currentSlug}/{leaf}[/...]` and `leaf` is in
 * PROGRAM_SCOPED_LEAVES, rebuild it with `newSlug`. Otherwise return null.
 * Used by the Header chip strip to compute the navigation target on click.
 */
export function programScopedTarget(
  pathname: string,
  newSlug: string,
): string | null {
  const parts = pathname.replace(/^\/+/, '').split('/').filter(Boolean);
  if (parts.length < 2) return null;
  const [, leaf, ...rest] = parts;
  if (!leaf || !PROGRAM_SCOPED_LEAVES.has(leaf)) return null;
  return '/' + [newSlug, leaf, ...rest].join('/');
}

/**
 * Pull the `:program` slug from the current URL when the path is
 * program-scoped. Used by useSyncSelectedFromUrl below and reusable for any
 * other consumer that needs the canonical program at a glance.
 */
export function programSlugFromPath(pathname: string): string | null {
  const parts = pathname.replace(/^\/+/, '').split('/').filter(Boolean);
  if (parts.length < 2) return null;
  const [slug, leaf] = parts;
  if (!slug || !leaf || !PROGRAM_SCOPED_LEAVES.has(leaf)) return null;
  return slug;
}

/**
 * Sync the URL's `:program` segment into `useSetupCampaigns.selectedCampaignId`.
 *
 * - URL is canonical (shareable links must land on the right campaign).
 * - When the user lands directly on `/fobt/distribution`, this hook pins
 *   FOBT in the store so the chip strip highlights it.
 * - Chip clicks call `selectCampaign(id)` AND `navigate()`; the navigate
 *   triggers this hook for free, so store + URL stay in lock-step without
 *   either being the "source of truth" (they mirror each other).
 * - When the URL slug doesn't match any active+non-archived campaign (e.g.
 *   the archived FOBT 2025 Fall slug or a typo), this hook does nothing —
 *   the existing selection is preserved.
 */
export function useSyncSelectedFromUrl(): void {
  const location = useLocation();
  const navigate = useNavigate();
  const campaigns = useSetupCampaigns((s) => s.campaigns);
  const selectedCampaignId = useSetupCampaigns((s) => s.selectedCampaignId);
  const selectCampaign = useSetupCampaigns((s) => s.selectCampaign);

  useEffect(() => {
    const slug = programSlugFromPath(location.pathname);
    if (!slug) return;
    const match = campaigns.find(
      (c) => c.active && !c.archived && routeSlugOf(c) === slug,
    );
    if (match) {
      if (match.id !== selectedCampaignId) selectCampaign(match.id);
      return;
    }
    // Stale URL slug (e.g. /fobt/* after the 2026-05-25 rename to /25fobt/).
    // Redirect to the currently selected campaign so URL and content stay in
    // lockstep — was previously a no-op which left the user looking at an
    // empty page with a misleading URL.
    const selected = campaigns.find((c) => c.id === selectedCampaignId);
    const fallback = selected ?? campaigns.find((c) => c.active && !c.archived);
    if (fallback) {
      const target = programScopedTarget(location.pathname, routeSlugOf(fallback));
      if (target) navigate(target, { replace: true });
    }
  }, [location.pathname, campaigns, selectedCampaignId, selectCampaign, navigate]);
}
